import type React from 'react';
import {useState} from 'react';
import {useOutletContext} from 'react-router';
import {Alert, Button, MenuItem, Paper, Skeleton, Stack, TextField, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {useShouldCostActions, useShouldCostQuery} from '../../hooks/useShouldCost';
import type {CategoryProfileOutletContext} from '../profileTypes';
import {AccuracyCard} from '../should-cost/AccuracyCard';
import {ActualizationCard} from '../should-cost/ActualizationCard';
import {ModelEditorDialog} from '../should-cost/ModelEditorDialog';
import {NewModelDialog} from '../should-cost/NewModelDialog';
import {PriceModelCard} from '../should-cost/PriceModelCard';
import {ProvenanceCard} from '../should-cost/ProvenanceCard';
import {PriceDriversSection} from '../should-cost/PriceDriversSection';

/**
 * Should cost (spec 4.9): price models priced from market indices, accuracy, post-negotiation actualization and model
 * provenance. Supplier quote comparisons are not shown (calibration, cost-logic review and landed cost were removed);
 * quotes themselves stay in the API for the next Should cost version, which brings back a way to enter them. Without a model: Start from a template / Start from scratch.
 */
export const ShouldCostTab: React.FC = () => {
    const {databagId, path, header} = useOutletContext<CategoryProfileOutletContext>();
    const {data, isLoading, isError} = useShouldCostQuery(databagId, path);
    const actions = useShouldCostActions(databagId, path);
    const [starting, setStarting] = useState<'template' | 'scratch' | null>(null);
    const [editing, setEditing] = useState(false);
    const [picked, setPicked] = useState<number | null>(null);

    if (isError) return <Alert severity="error">Could not load should cost.</Alert>;
    if (isLoading || !data) return <Stack gap={2}><Skeleton variant="rounded" height={120}/><Skeleton variant="rounded" height={280}/></Stack>;

    const model = data.models.find(m => m.id === picked) ?? data.models[0];
    const newDialog = starting && (
        <NewModelDialog templates={data.options.templates} category={header.name} start={starting}
                        onSubmit={actions.addModel} onClose={() => setStarting(null)}/>
    );

    // Price drivers come first: a model's cost lines move with them
    const drivers = <PriceDriversSection databagId={databagId} path={path} data={data}/>;

    if (!model) {
        return (
            <Stack gap={3}>
                {drivers}
                <Paper variant="outlined" sx={{p: 4, textAlign: 'center'}}>
                    <Typography variant="h6">No price model for this category yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{maxWidth: 560, mx: 'auto', my: 1}}>
                        A price model splits one unit into cost lines and ties each line to a price driver (an index or your own
                        monthly prices), so the should cost moves with them. Compare it with supplier quotes to find the gap worth negotiating.
                    </Typography>
                    {data.canEdit && (
                        <Stack direction="row" gap={1} justifyContent="center" sx={{mt: 2}}>
                            <Button variant="contained" onClick={() => setStarting('template')}>Start from a template</Button>
                            <Button variant="outlined" onClick={() => setStarting('scratch')}>Start from scratch</Button>
                        </Stack>
                    )}
                    {newDialog}
                </Paper>
            </Stack>
        );
    }

    return (
        <Stack gap={3}>
            {drivers}
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                {data.models.length > 1 && (
                    <TextField select size="small" label="Price model" value={model.id} sx={{minWidth: 280}}
                               onChange={e => setPicked(Number(e.target.value))}>
                        {data.models.map(m => <MenuItem key={m.id} value={m.id}>{`${m.name}${m.active ? ' (active)' : ''}`}</MenuItem>)}
                    </TextField>
                )}
                {data.canEdit && <Button size="small" startIcon={<Add/>} onClick={() => setStarting('template')}>New price model</Button>}
            </Stack>
            <PriceModelCard model={model} canEdit={data.canEdit} onEdit={() => setEditing(true)}
                            onActivate={() => void actions.setActive(model)}
                            onRemove={() => {
                                setPicked(null);
                                void actions.removeModel(model);
                            }}/>
            <AccuracyCard model={model}/>
            <ActualizationCard model={model} canEdit={data.canEdit} actions={actions}/>
            <ProvenanceCard model={model}/>
            {editing && (
                <ModelEditorDialog model={model} options={data.options} drivers={data.drivers} onSubmit={change => actions.updateModel(model, change)}
                                   onClose={() => setEditing(false)}/>
            )}
            {newDialog}
        </Stack>
    );
};
